import { useEffect, useMemo, useCallback } from 'react'
import { alpha, styled, useTheme } from '@mui/material/styles'
import Menu, { MenuProps } from '@mui/material/Menu'
import Drawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import CheckIcon from '@mui/icons-material/Check'
import Link from '@mui/material/Link'
import CloseIcon from '@mui/icons-material/Close'
import { Link as RouterLink } from 'react-router-dom'
import Divider from '@mui/material/Divider'
import { useSizes } from '@/hooks/useSizes'
import { useNotifications } from '@/pages/NotificationCenter/hooks/useNotifications'
import { useNotificationActions } from '@/pages/NotificationCenter/hooks/useNotificationActions'
import NotificationItem from '../NotificationCenter/NotificationItem'
import {
  EmptyState,
  LoadingState,
  ErrorState,
} from '../NotificationCenter/NotificationStates'

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 8,
    marginTop: theme.spacing(0.5),
    minWidth: 400,
    maxWidth: 400,
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: 0,
    },
  },
}))

const HeaderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2, 1, 2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0.5, 2, 1, 2),
  },
}))

const NotificationList = styled(Box)(({ theme }) => ({
  minHeight: '224px',
  maxHeight: '500px',
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    flex: 1,
    minHeight: 'unset',
    maxHeight: 'unset',
  },
}))

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '80vh',
    boxShadow: theme.shadows[8],
  },
}))

interface NotificationDropdownPanelProps {
  open: boolean
  anchorEl: HTMLElement | null
  onClose: () => void
}

const NotificationDropdownPanel = ({
  open,
  anchorEl,
  onClose,
}: NotificationDropdownPanelProps) => {
  const theme = useTheme()
  const { isLessMd } = useSizes()
  const isMobile = isLessMd

  const {
    notifications,
    loading,
    error,
    unreadCount,
    hasInitiallyLoaded,
    fetchNotifications,
    realtime,
  } = useNotifications()

  const { markAllAsRead, loading: actionLoading } = useNotificationActions({
    onSuccess: () => {
      // Optional: Show success feedback
    },
    onError: () => {
      // Handle error silently or show user-friendly feedback
    },
  })

  // Smart fetch: only fetch when necessary
  // NOTE: NotificationContext has its own smart sync that handles initial load.
  // This only fetches when the dropdown opens and there's a specific need (error or Pusher disconnected).
  useEffect(() => {
    if (open) {
      // Don't fetch if user is not authenticated (notifications will be empty and service null)
      // Check if realtime service exists as a proxy for authentication status
      const isAuthenticated = realtime.connected !== undefined

      if (!isAuthenticated) {
        return
      }

      // Don't fetch if context is currently loading (avoid race conditions)
      if (loading) {
        return
      }

      // Only fetch if Pusher is disconnected and we need fresh data
      // Don't fetch just because notifications.length === 0 - the context smart sync handles initial load
      // Don't auto-retry on error - let user manually retry via error state button
      const shouldFetch =
        !realtime.connected && hasInitiallyLoaded && error === null

      if (shouldFetch) {
        fetchNotifications({ per_page: 5 }) // Show only recent 5 notifications
      }
    }
  }, [
    open,
    loading,
    hasInitiallyLoaded,
    realtime.connected,
    error,
    fetchNotifications,
  ])

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead()
  }, [markAllAsRead])

  // Memoize the sliced notifications to prevent unnecessary recalculations
  const recentNotifications = useMemo(
    () => notifications.slice(0, 5),
    [notifications]
  )

  const areNotificationsEmpty = recentNotifications.length === 0

  const showNotificationHeader = !areNotificationsEmpty || isMobile

  // Shared content component - wrapped in Box to avoid Fragment issues with MUI Menu
  const notificationContent = (
    <Box
      sx={
        isMobile
          ? {
              display: 'flex',
              flexDirection: 'column',
              height: areNotificationsEmpty ? '50vh' : '80vh',
              overflow: 'hidden',
            }
          : undefined
      }
    >
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            pt: 1.5,
            height: '72px',
          }}
        >
          <Typography variant="subtitle1" component="h1">
            Notifications
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon
              sx={{
                fontSize: theme.typography.h6.fontSize,
                color: alpha(theme.palette.common.black, 0.87),
              }}
            />
          </IconButton>
        </Box>
      )}
      {showNotificationHeader && (
        <HeaderContainer>
          {!isMobile ? (
            <Typography variant="subtitle2" component="h2">
              Notifications ({unreadCount})
            </Typography>
          ) : (
            !areNotificationsEmpty && (
              <Typography variant="body2" component="h2" color="text.secondary">
                {unreadCount} unread notifications
              </Typography>
            )
          )}
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllAsRead}
              disabled={actionLoading.bulkOperating}
              data-testid="mark-all-read-button"
              variant="text"
            >
              <CheckIcon
                sx={{ fontSize: 12, color: 'text.secondary', mr: 1 }}
              />
              <Typography variant="captionBold">
                {actionLoading.bulkOperating
                  ? 'Marking...'
                  : 'Mark all as read'}
              </Typography>
            </Button>
          )}
        </HeaderContainer>
      )}

      <NotificationList>
        {(() => {
          if (loading) {
            return <LoadingState />
          }
          if (error) {
            return (
              <ErrorState
                error={error.message || 'Failed to load notifications'}
                onRetry={() => fetchNotifications({ per_page: 5 })}
              />
            )
          }
          if (areNotificationsEmpty) {
            return <EmptyState />
          }
          return recentNotifications.map((notification, index) => (
            <Box key={notification.id}>
              <NotificationItem notification={notification} onClick={onClose} />
              {index < recentNotifications.length - 1 && (
                <Divider sx={{ ml: 2, mr: 2 }} />
              )}
            </Box>
          ))
        })()}
      </NotificationList>

      {recentNotifications.length > 0 && (
        <Box
          sx={{
            padding: theme.spacing(1.5, 2),
            borderTop: `1px solid ${theme.palette.divider}`,
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: {
              xs: 88,
              md: 52,
            },
          }}
        >
          <Link
            component={RouterLink}
            to="/notifications"
            underline="hover"
            onClick={onClose}
            data-testid="view-all-notifications-link"
          >
            <Typography variant="captionBold">
              View All Notifications
            </Typography>
          </Link>
        </Box>
      )}
    </Box>
  )

  // Conditionally render Drawer for mobile or Menu for desktop
  if (isMobile) {
    return (
      <StyledDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        data-testid="notification-drawer"
      >
        {notificationContent}
      </StyledDrawer>
    )
  }

  return (
    <StyledMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      data-testid="notification-dropdown"
    >
      {notificationContent}
    </StyledMenu>
  )
}

export default NotificationDropdownPanel
